/**
 * The permission constants the server enforces.
 *
 * Mirrors `com.company.dam.platform.security.Permissions`. Used to decide what to *show*.
 *
 * This is not a security boundary. The server checks every request against the same permission set
 * regardless of what this client renders; hiding a button is a courtesy that stops users being
 * offered actions that would be refused, nothing more. A user who calls the API directly gains
 * exactly nothing by bypassing the interface.
 */
export const PERMISSIONS = {
  ORG_READ: 'ORG_READ',
  ORG_WRITE: 'ORG_WRITE',

  EMPLOYEE_READ: 'EMPLOYEE_READ',
  EMPLOYEE_WRITE: 'EMPLOYEE_WRITE',

  USER_READ: 'USER_READ',
  USER_WRITE: 'USER_WRITE',
  ROLE_READ: 'ROLE_READ',
  ROLE_WRITE: 'ROLE_WRITE',

  SOFTWARE_LICENCE_READ: 'SOFTWARE_LICENCE_READ',
  SOFTWARE_LICENCE_WRITE: 'SOFTWARE_LICENCE_WRITE',

  ASSET_READ: 'ASSET_READ',
  ASSET_READ_OWN: 'ASSET_READ_OWN',
  ASSET_WRITE: 'ASSET_WRITE',
  ASSET_TAG: 'ASSET_TAG',
  ASSET_PREPARE: 'ASSET_PREPARE',
  ASSET_ASSIGN: 'ASSET_ASSIGN',
  ASSET_RETURN: 'ASSET_RETURN',
  ASSET_RETIRE: 'ASSET_RETIRE',
  ASSET_TRANSITION_OVERRIDE: 'ASSET_TRANSITION_OVERRIDE',
  ASSET_DECOMMISSION_REQUEST: 'ASSET_DECOMMISSION_REQUEST',
  ASSET_DECOMMISSION_APPROVE: 'ASSET_DECOMMISSION_APPROVE',
  ASSET_DECOMMISSION_SANITISE: 'ASSET_DECOMMISSION_SANITISE',
  ASSET_DECOMMISSION_DISPOSE: 'ASSET_DECOMMISSION_DISPOSE',
  ASSET_STOCKTAKE_WRITE: 'ASSET_STOCKTAKE_WRITE',

  ACCEPTANCE_SUBMIT: 'ACCEPTANCE_SUBMIT',
  ACCEPTANCE_READ: 'ACCEPTANCE_READ',

  AUDIT_READ: 'AUDIT_READ',
  AUDIT_VERIFY: 'AUDIT_VERIFY',
  AUDIT_EXPORT: 'AUDIT_EXPORT',

  INTEGRATION_READ: 'INTEGRATION_READ',
  INTEGRATION_MANAGE: 'INTEGRATION_MANAGE',
  OUTBOX_READ: 'OUTBOX_READ',
  OUTBOX_REPLAY: 'OUTBOX_REPLAY',

  REPORT_READ: 'REPORT_READ',
  ENDPOINT_READ: 'ENDPOINT_READ',
  ENDPOINT_MANAGE: 'ENDPOINT_MANAGE',
  DEVICE_ALERT_SEND: 'DEVICE_ALERT_SEND',
  DEVICE_UNINSTALL_AUTHORIZE: 'DEVICE_UNINSTALL_AUTHORIZE',
  ENDPOINT_LINK: 'ENDPOINT_LINK',
  ENDPOINT_CONTROL: 'ENDPOINT_CONTROL',
  ENDPOINT_COMMAND_APPROVE: 'ENDPOINT_COMMAND_APPROVE',
  ENDPOINT_LOCATION_READ: 'ENDPOINT_LOCATION_READ',
  POLICY_READ: 'POLICY_READ',
  POLICY_WRITE: 'POLICY_WRITE',
  POLICY_DEPLOY: 'POLICY_DEPLOY',
  POLICY_ENFORCE: 'POLICY_ENFORCE',
  DISCOVERY_READ: 'DISCOVERY_READ',
  DISCOVERY_SCAN: 'DISCOVERY_SCAN',
  DISCOVERY_MANAGE: 'DISCOVERY_MANAGE',
  NETWORK_MONITOR_READ: 'NETWORK_MONITOR_READ',
  NETWORK_MONITOR_MANAGE: 'NETWORK_MONITOR_MANAGE',
  NETWORK_MONITOR_LINK_ASSET: 'NETWORK_MONITOR_LINK_ASSET',
  IMPORT_READ: 'IMPORT_READ',
  IMPORT_VALIDATE: 'IMPORT_VALIDATE',
  IMPORT_APPLY: 'IMPORT_APPLY',
  TICKET_READ: 'TICKET_READ',
  TICKET_READ_OWN: 'TICKET_READ_OWN',
  TICKET_CREATE: 'TICKET_CREATE',
  TICKET_WRITE: 'TICKET_WRITE',
  TICKET_ADMIN: 'TICKET_ADMIN',

  PROCUREMENT_READ: 'PROCUREMENT_READ',
  PROCUREMENT_WRITE: 'PROCUREMENT_WRITE',
  PROCUREMENT_APPROVE: 'PROCUREMENT_APPROVE',
  PROCUREMENT_RECEIVE: 'PROCUREMENT_RECEIVE',

  PREPARATION_READ: 'PREPARATION_READ',
  PREPARATION_RUN: 'PREPARATION_RUN',
  PREPARATION_TEMPLATE_WRITE: 'PREPARATION_TEMPLATE_WRITE',

  NOTIFICATION_READ_OWN: 'NOTIFICATION_READ_OWN',
  NOTIFICATION_ADMIN: 'NOTIFICATION_ADMIN',

  CATALOG_WRITE: 'CATALOG_WRITE',

  CONSUMABLE_READ: 'CONSUMABLE_READ',
  CONSUMABLE_WRITE: 'CONSUMABLE_WRITE',
  CONSUMABLE_ISSUE: 'CONSUMABLE_ISSUE',

  // The platform operator, across every company.
  PLATFORM_READ: 'PLATFORM_READ',
  PLATFORM_MANAGE: 'PLATFORM_MANAGE',
  PLATFORM_BILLING: 'PLATFORM_BILLING',
  PLATFORM_MODULES: 'PLATFORM_MODULES',
  // A company's view of its own licence.
  SUBSCRIPTION_READ: 'SUBSCRIPTION_READ',

  // Maintenance: reading, working orders, and planning recurring work are separate.
  MAINTENANCE_READ: 'MAINTENANCE_READ',
  MAINTENANCE_WRITE: 'MAINTENANCE_WRITE',
  MAINTENANCE_PLAN: 'MAINTENANCE_PLAN',
  // Warranty and AMC contracts.
  CONTRACT_READ: 'CONTRACT_READ',
  CONTRACT_WRITE: 'CONTRACT_WRITE',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
